import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Divider } from '@hivebrite/design-system/Divider';
import { RadioInput } from '@hivebrite/design-system/Inputs';

import { STRIPE_PAYMENT_TYPES } from 'hb-react/shared/components/Payment/constants';
import AmericanExpress from 'hb-react/shared/icons/Payments/AmericanExpress';
import Bancontact from 'hb-react/shared/icons/Payments/Bancontact';
import Ideal from 'hb-react/shared/icons/Payments/Ideal';
import Maestro from 'hb-react/shared/icons/Payments/Maestro';
import Mastercard from 'hb-react/shared/icons/Payments/Mastercard';
import Sepa from 'hb-react/shared/icons/Payments/Sepa';
import Visa from 'hb-react/shared/icons/Payments/Visa';

const Icons = ({ icons }) =>
  Object.keys(icons).map((key) => {
    const IconComponent = icons[key];

    return <IconComponent width={32} height={24} key={key} />;
  });

const SUPPORTED_METHODS = {
  [STRIPE_PAYMENT_TYPES.CARD]: {
    value: STRIPE_PAYMENT_TYPES.CARD,
    title: s__('Stripe|Credit Card'),
    icons: {
      Visa,
      AmericanExpress,
      Maestro,
      Mastercard,
    },
    defaultChecked: true,
  },
  [STRIPE_PAYMENT_TYPES.SEPA_DEBIT]: {
    value: STRIPE_PAYMENT_TYPES.SEPA_DEBIT,
    title: s__('Stripe|SEPA Direct Debit'),
    icons: { Sepa },
  },
  [STRIPE_PAYMENT_TYPES.IDEAL]: {
    value: STRIPE_PAYMENT_TYPES.IDEAL,
    title: s__('Stripe|iDEAL'),
    icons: { Ideal },
  },
  [STRIPE_PAYMENT_TYPES.BANCONTACT]: {
    value: STRIPE_PAYMENT_TYPES.BANCONTACT,
    title: s__('Stripe|Bancontact'),
    icons: { Bancontact },
  },
};

const PaymentMethods = ({ methods = [], selected, onChange }) =>
  methods.map(({ value, title, icons }) => (
    <StyledBox flexDirection="column" key={value}>
      <RadioInput
        name="payment_type"
        value={value}
        id={value}
        type="radio"
        checked={value === selected}
        onChange={() => onChange(value)}
        labelMultiline
        label={
          <Box width="100%" alignItems="center">
            <Box>{title}</Box>
            <Box justifyContent="flex-end">
              <Icons icons={icons} />
            </Box>
          </Box>
        }
      />
      <Divider my={4} />
    </StyledBox>
  ));

export const PaymentMethodSelector = ({
  selectedPaymentMethod = STRIPE_PAYMENT_TYPES.CARD,
  gateway,
  onSelect,
}) => {
  const { available_payment_options: paymentOptions } = gateway;
  const [selected, setSelected] = useState(selectedPaymentMethod);

  useEffect(() => onSelect?.(selected), [selected]);

  const paymentMethods = useMemo(
    () =>
      Object.entries(paymentOptions)
        .filter(([_, enabled]) => enabled)
        .reduce(
          (methods, [method]) => [...methods, SUPPORTED_METHODS[method]],
          [],
        )
        .filter(Boolean),
    [paymentOptions],
  );

  return (
    <Box width="100%">
      <Box
        flexDirection="column"
        width="100%"
        data-testid="select-payment-method"
      >
        <Box font="body-regular-asphalt">
          {s__('Stripe|Select a payment method')}
        </Box>
        <Divider my={4} />
        <Box flexDirection="column">
          <PaymentMethods
            methods={paymentMethods}
            selected={selected}
            onChange={setSelected}
          />
        </Box>
      </Box>
    </Box>
  );
};

PaymentMethodSelector.propTypes = {
  onSelect: PropTypes.func,
  selectedPaymentMethod: PropTypes.oneOf(Object.values(STRIPE_PAYMENT_TYPES)),
  gateway: PropTypes.shape({
    available_payment_options: PropTypes.shape({
      [STRIPE_PAYMENT_TYPES.CARD]: PropTypes.bool.isRequired,
      [STRIPE_PAYMENT_TYPES.IDEAL]: PropTypes.bool.isRequired,
      [STRIPE_PAYMENT_TYPES.SEPA_DEBIT]: PropTypes.bool.isRequired,
      [STRIPE_PAYMENT_TYPES.BANCONTACT]: PropTypes.bool.isRequired,
    }),
  }).isRequired,
};

const StyledBox = styled(Box)`
  label {
    width: 100%;
    display: flex;
  }

  div {
    flex-grow: 1;
  }
`;
